import { Injectable } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { ModalController, Platform } from '@ionic/angular';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { UpdateModalComponent } from '../components/update-modal/update-modal.component';
import packageJson from '../../../package.json';

interface VersionCheckResponse {
  apiVersion: string;
  minAppVersion: string;
  name: string;
  compatible?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class VersionService {
  private updateModalShown = false;
  private http: HttpClient;

  constructor(
    private handler: HttpBackend,
    private modalController: ModalController,
    private platform: Platform
  ) {
    this.http = new HttpClient(this.handler);
  }

  /**
   * Obtiene la versión actual de la app desde package.json
   */
  getAppVersion(): string {
    return packageJson.version;
  }

  /**
   * Obtiene información de versión desde la API
   * La API es quien determina si la versión es compatible
   * @returns Información de versión desde el servidor
   */
  async getVersionInfo(): Promise<VersionCheckResponse | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<VersionCheckResponse>(`${environment.apiUrl}version`)
      );
      return response;
    } catch (error) {
      console.error('Error getting version info:', error);
      return null;
    }
  }

  /**
   * Muestra el modal de actualización cuando la versión no es compatible
   * Este método es llamado automáticamente por el interceptor cuando recibe error 426
   * @param errorData Datos del error 426 recibido de la API
   */
  async showUpdateModal(errorData?: any): Promise<void> {
    // Evitar mostrar múltiples modales simultáneamente
    if (this.updateModalShown) {
      console.log('Update modal already shown, skipping...');
      return;
    }
    
    this.updateModalShown = true;

    try {
      const modal = await this.modalController.create({
        component: UpdateModalComponent,
        backdropDismiss: false, // No permitir cerrar tocando fuera
        componentProps: {
          currentVersion: this.getAppVersion(),
          minVersion: errorData?.minVersion,
          apiVersion: errorData?.currentApiVersion || errorData?.apiVersion
        }
      });

      await modal.present();
      
      // Resetear flag cuando se cierre el modal
      await modal.onDidDismiss();
      this.updateModalShown = false;
    } catch (error) {
      console.error('Error showing update modal:', error);
      this.updateModalShown = false;
    }
  }

  /**
   * Obtiene información de la plataforma actual
   */
  getPlatformInfo(): { isIOS: boolean; isAndroid: boolean; isWeb: boolean } {
    return {
      isIOS: this.platform.is('ios'),
      isAndroid: this.platform.is('android'),
      isWeb: this.platform.is('mobileweb') || this.platform.is('desktop')
    };
  }
}
